/**
 * The WT-3 tabs of a job (fieldiq-api#1248): Readings (with the test periods),
 * Chart, Flags and Daily volumes. The Reports tab is ReportsSection.tsx.
 *
 * A VALUE IS SHOWN AS IT WAS TYPED, WITH ITS UNIT — `entered`, never the metric
 * copy. Figures the API added up or converted (chart points, volume totals)
 * are shown from the API's `display` and `displayUnit`. Nothing here converts
 * a unit or adds a column of numbers.
 *
 * WHO SEES WHAT (mirrors fieldiq-api lib/wt-roles.ts, for hiding a button):
 *   Add / correct a reading   WT_WORK (dispatch roles + field_worker on the crew)
 *   Periods, flag Ack, daily volumes   WT_DISPATCH (the office)
 * A 403 that still arrives is shown as a refusal, never as success.
 */
import React, { useMemo, useState } from 'react';
import { CheckCircle2, FileSpreadsheet, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  useAckWtFlag,
  useCorrectWtReading,
  useCreateWtPeriod,
  useCreateWtReading,
  useSaveWtDailyVolume,
  useUpdateWtPeriod,
  useWtDailyVolumes,
  useWtFlags,
  useWtPeriods,
  useWtReadingFields,
  useWtReadings,
  useWtSeries,
} from '../../hooks/useWellTesting';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  withUnit,
  wtCamelKey,
  wtDay,
  wtFlagText,
  wtQuantity,
  wtRefusal,
  wtShown,
  wtUnitLabel,
  WT_READING_REFUSALS,
  WT_VOLUME_REFUSALS,
} from '../../lib/well-testing';
import type {
  WtCatalog,
  WtDailyVolume,
  WtDisposition,
  WtFlag,
  WtJobDetail,
  WtMeta,
  WtReading,
  WtReadingField,
} from '../../types/well-testing';
import { useOpenRecord } from '../downhole/recordPage';
import { ReadingForm, type ReadingFormInitial, type ReadingFormTank } from './ReadingForm';
import {
  WT_INPUT,
  WT_PRIMARY,
  WT_SECONDARY,
  WtBadge,
  WtChip,
  WtChipRow,
  WtDialog,
  WtEmpty,
  WtField,
  WtFormError,
  WtLoading,
  WtPanel,
  WtRegister,
  WtSpec,
  type WtColumn,
} from './WtBits';

const when = (iso: string | null | undefined, tz: string | null | undefined) => formatInstant(iso, tz) ?? '—';

/** The job's test tanks, the units a tank gauge is entered against. */
export function jobTanks(job: WtJobDetail): ReadingFormTank[] {
  return job.equipment
    .filter((e) => e.componentKind === 'test_tank' && !e.offDate)
    .map((e) => ({ equipmentId: e.equipmentId, label: [e.equipmentName, e.unitNumber].filter(Boolean).join(' · ') || 'Test tank' }));
}

/** The tenant's tank-gauge unit: the default the API applies when a body names none. */
export const tankDefaultUnit = (meta: WtMeta | null | undefined) => (meta?.unitSystem === 'imperial' ? 'ft_in' : 'mm');

/** A reading's typed values as the correction form's starting point. */
export function readingInitial(r: WtReading, fields: WtReadingField[]): ReadingFormInitial {
  const entries: ReadingFormInitial['entries'] = {};
  for (const f of fields) {
    const v = r.values[wtCamelKey(f.key)];
    if (v) entries[f.key] = { value: v.entered.value, unit: v.entered.unit };
  }
  const tanks: ReadingFormInitial['tanks'] = {};
  for (const t of r.tanks) tanks[t.equipmentId] = { value: t.level.entered.value, unit: t.level.entered.unit };
  return { entries, tanks };
}

const tankText = (t: WtReading['tanks'][number]) =>
  `${t.equipmentName || t.unitNumber || 'Tank'}: ${wtQuantity(t.level)}`;

function SourceBadge({ r }: { r: WtReading }) {
  if (!r.live) return <WtBadge skin={{ label: 'Corrected', fill: 'bg-fi-signal-slate', rail: '' }} value="superseded" />;
  if (r.correctedFromId) return <WtBadge skin={{ label: 'Correction', fill: 'bg-fi-signal-blue', rail: '' }} value="correction" />;
  return <span className="text-sm text-fieldiq-text">{r.source === 'phone' ? 'Phone' : r.source === 'office' ? 'Office' : r.source === 'logger_import' ? 'Logger file' : r.source || '—'}</span>;
}

const SHOW_LAST = [12, 24, 48, 0] as const;

// ═══ Readings ═════════════════════════════════════════════════════════════════

export function ReadingsSection({
  job,
  meta,
  catalog,
  canWork,
  canDispatch,
  loggable,
}: {
  job: WtJobDetail;
  meta: WtMeta | null;
  catalog: WtCatalog | null;
  canWork: boolean;
  canDispatch: boolean;
  loggable: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const [last, setLast] = useState<number>(12);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState(false);
  const [correctError, setCorrectError] = useState<string | null>(null);
  const fieldsQuery = useWtReadingFields();
  const query = useWtReadings(job.id, showAll);
  const create = useCreateWtReading();
  const correct = useCorrectWtReading();
  const fields = fieldsQuery.data?.data?.fields ?? [];
  const tz = meta?.timeZone ?? null;
  const tanks = jobTanks(job);
  // WT-3b: the logger import is its own page (office only — the API 403s a field hand).
  const openRecord = useOpenRecord();

  // Newest first, then the last N (the API sends oldest first).
  const rows = useMemo(() => {
    const all = [...(query.data?.data ?? [])].reverse();
    return last ? all.slice(0, last) : all;
  }, [query.data, last]);
  const total = query.data?.data?.length ?? 0;
  const open = rows.find((r) => r.id === openId) ?? null;

  // A column per field some shown row recorded, in the API's field order.
  const shownFields = fields.filter((f) => rows.some((r) => r.values[wtCamelKey(f.key)]));
  const columns: WtColumn<WtReading>[] = [
    { key: 'readAt', header: 'Read at', render: (r) => <span className="whitespace-nowrap font-extrabold text-white">{when(r.readAt, tz)}</span> },
    ...shownFields.map<WtColumn<WtReading>>((f) => ({
      key: f.key,
      header: f.label,
      align: 'right',
      render: (r) => <span className="whitespace-nowrap">{wtQuantity(r.values[wtCamelKey(f.key)])}</span>,
    })),
    ...(rows.some((r) => r.tanks.length)
      ? [{ key: 'tanks', header: 'Tanks', render: (r: WtReading) => (r.tanks.length ? r.tanks.map(tankText).join(' · ') : '—') }]
      : []),
    { key: 'source', header: 'Source', render: (r) => <SourceBadge r={r} /> },
    { key: 'flags', header: 'Flags', align: 'right', render: (r) => (r.flags.length ? String(r.flags.length) : '—') },
  ];

  return (
    <>
      <PeriodsPanel job={job} meta={meta} canDispatch={canDispatch} loggable={loggable} />

      <WtPanel
        eyebrow="Readings"
        title="Readings"
        testId="wt-readings"
        actions={
          loggable && !adding && (canWork || canDispatch) ? (
            <div className="flex flex-wrap gap-2">
              {canDispatch && (
                <button
                  type="button"
                  className={WT_SECONDARY}
                  onClick={() => openRecord(`/well-testing/jobs/${job.id}/readings/import`)}
                  data-testid="wt-import-logger"
                >
                  <FileSpreadsheet className="size-4" aria-hidden="true" />
                  Import logger file
                </button>
              )}
              {canWork && (
                <button type="button" className={WT_PRIMARY} onClick={() => { setAddError(null); setAdding(true); }} data-testid="wt-add-reading">
                  <Plus className="size-4" aria-hidden="true" />
                  Add reading
                </button>
              )}
            </div>
          ) : undefined
        }
      >
        <div className="flex flex-wrap items-center gap-4 border-b border-fieldiq-border px-4 py-3">
          <label className="flex items-center gap-2 text-sm text-fieldiq-text">
            Show
            <select className={`${WT_INPUT} w-auto`} aria-label="How many readings" value={last} onChange={(e) => setLast(Number(e.target.value))}>
              {SHOW_LAST.map((n) => (
                <option key={n} value={n}>
                  {n ? `Last ${n}` : 'All'}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-fieldiq-text">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} aria-label="Show corrections" />
            Show corrections
          </label>
          {!query.isLoading && total > 0 && (
            <span className="text-xs text-fieldiq-muted">
              {rows.length} of {total} {showAll ? 'rows, corrected ones included' : 'live readings'}
            </span>
          )}
        </div>

        {adding && (
          <div className="border-b border-fieldiq-border" data-testid="wt-add-reading-panel">
            {fieldsQuery.isLoading ? (
              <WtLoading label="Loading the reading fields" />
            ) : queryLoadFailure(fieldsQuery) ? (
              <LoadFailureNotice error={queryFailureError(fieldsQuery)} kind={queryLoadFailure(fieldsQuery) ?? 'unreachable'} subject="the reading fields" />
            ) : (
              <ReadingForm
                fields={fields}
                quantityUnits={catalog?.quantityUnits}
                tanks={tanks}
                tankDefaultUnit={tankDefaultUnit(meta)}
                mode="new"
                pending={create.isPending}
                error={addError}
                submitLabel="Save reading"
                onCancel={() => setAdding(false)}
                onSubmit={(body) =>
                  create.mutate(
                    { jobId: job.id, body },
                    {
                      onSuccess: () => {
                        toast.success('Reading saved');
                        setAdding(false);
                      },
                      onError: (err) => setAddError(wtRefusal(err, 'The reading could not be saved.', WT_READING_REFUSALS)),
                    },
                  )
                }
              />
            )}
          </div>
        )}

        <WtRegister
          query={query}
          rows={rows}
          columns={columns}
          rail={(r) => (r.live ? 'border-l-transparent' : 'border-l-fi-steel')}
          onOpen={(r) => { setOpenId(r.id); setCorrecting(false); setCorrectError(null); }}
          subject="the readings"
          testId="wt-reading"
          minWidth="min-w-[960px]"
          empty={<WtEmpty>No reading has been entered on this job yet.</WtEmpty>}
          card={(r) => (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-extrabold text-white">{when(r.readAt, tz)}</p>
                <SourceBadge r={r} />
              </div>
              <p className="mt-1 text-xs text-fieldiq-text">
                {shownFields
                  .filter((f) => r.values[wtCamelKey(f.key)])
                  .slice(0, 4)
                  .map((f) => `${f.label} ${wtQuantity(r.values[wtCamelKey(f.key)])}`)
                  .join(' · ') || '—'}
              </p>
            </>
          )}
        />
      </WtPanel>

      {open && (
        <WtPanel
          eyebrow="Reading"
          title={when(open.readAt, tz)}
          testId="wt-reading-detail"
          actions={
            <div className="flex gap-2">
              {canWork && loggable && open.live && !correcting && (
                <button type="button" className={WT_PRIMARY} onClick={() => { setCorrectError(null); setCorrecting(true); }} data-testid="wt-correct-reading">
                  Correct
                </button>
              )}
              <button type="button" className={WT_SECONDARY} onClick={() => setOpenId(null)}>
                Close
              </button>
            </div>
          }
        >
          {correcting ? (
            <ReadingForm
              key={open.id}
              fields={fields}
              quantityUnits={catalog?.quantityUnits}
              tanks={tanks}
              tankDefaultUnit={tankDefaultUnit(meta)}
              initial={readingInitial(open, fields)}
              mode="correct"
              pending={correct.isPending}
              error={correctError}
              submitLabel="Save correction"
              testId="wt-correct-form"
              onCancel={() => setCorrecting(false)}
              onSubmit={(body) =>
                correct.mutate(
                  { id: open.id, body: body as { correction_reason: string } },
                  {
                    onSuccess: (res) => {
                      toast.success('Correction saved');
                      setCorrecting(false);
                      setOpenId(res.data?.id ?? null);
                    },
                    onError: (err) => setCorrectError(wtRefusal(err, 'The correction could not be saved.', WT_READING_REFUSALS)),
                  },
                )
              }
            />
          ) : (
            <>
              {!open.live && (
                <p className="border-b border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                  This reading was corrected. The newer reading replaces it; it is kept as entered.
                </p>
              )}
              {open.correctionReason && (
                <p className="border-b border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text" data-testid="wt-correction-reason">
                  <span className="font-bold">Correction: </span>
                  {open.correctionReason}
                </p>
              )}
              <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                {fields
                  .filter((f) => open.values[wtCamelKey(f.key)])
                  .map((f) => (
                    <WtSpec key={f.key} label={f.label} value={wtQuantity(open.values[wtCamelKey(f.key)])} mono />
                  ))}
                {open.tanks.map((t) => (
                  <WtSpec key={t.equipmentId} label={t.equipmentName || 'Tank'} value={wtQuantity(t.level)} mono />
                ))}
                <WtSpec label="Load recovered" value={withUnit(open.loadRecoveredPct, '%')} mono />
                <WtSpec label="Entered by" value={open.enteredByName || '—'} />
                <WtSpec label="Source" value={<SourceBadge r={open} />} />
              </dl>
              {open.notes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{open.notes}</p>}
            </>
          )}
        </WtPanel>
      )}
    </>
  );
}

// ═══ Test periods ═════════════════════════════════════════════════════════════

const PERIOD_KINDS = [
  { value: 'cleanup', label: 'Cleanup' },
  { value: 'flow', label: 'Flow' },
  { value: 'shut_in', label: 'Shut-in' },
  { value: 'build_up', label: 'Build-up' },
  { value: 'drawdown', label: 'Drawdown' },
];
const periodLabel = (k: string) => PERIOD_KINDS.find((p) => p.value === k)?.label ?? k;

function PeriodsPanel({ job, meta, canDispatch, loggable }: { job: WtJobDetail; meta: WtMeta | null; canDispatch: boolean; loggable: boolean }) {
  const query = useWtPeriods(job.id);
  const create = useCreateWtPeriod();
  const update = useUpdateWtPeriod();
  const [starting, setStarting] = useState(false);
  const [kind, setKind] = useState('cleanup');
  const [startedAt, setStartedAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const tz = meta?.timeZone ?? null;
  const periods = query.data?.data ?? [];
  const openPeriod = periods.find((p) => p.open) ?? null;
  const canWrite = canDispatch && loggable;

  return (
    <WtPanel
      eyebrow="Test periods"
      title={openPeriod ? `${periodLabel(openPeriod.kind)} since ${when(openPeriod.startedAt, tz)}` : 'Test periods'}
      testId="wt-periods"
      actions={
        canWrite && !starting ? (
          <div className="flex gap-2">
            {openPeriod && (
              <button
                type="button"
                className={WT_SECONDARY}
                disabled={update.isPending}
                data-testid="wt-end-period"
                onClick={() =>
                  update.mutate(
                    { id: openPeriod.id, body: { ended_at: new Date().toISOString() } },
                    { onSuccess: () => toast.success('Period ended'), onError: (err) => setError(wtRefusal(err, 'The period could not be ended.')) },
                  )
                }
              >
                End period
              </button>
            )}
            <button type="button" className={WT_PRIMARY} onClick={() => { setError(null); setStarting(true); }} data-testid="wt-start-period">
              <Plus className="size-4" aria-hidden="true" />
              Start period
            </button>
          </div>
        ) : undefined
      }
    >
      {error && !starting && (
        <div className="px-4 pt-3">
          <WtFormError message={error} />
        </div>
      )}
      {starting && (
        <form
          className="space-y-3 border-b border-fieldiq-border px-4 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            create.mutate(
              { jobId: job.id, body: { kind, ...(startedAt ? { started_at: startedAt } : {}) } },
              {
                onSuccess: () => {
                  toast.success('Period started');
                  setStarting(false);
                  setStartedAt('');
                },
                onError: (err) => setError(wtRefusal(err, 'The period could not be started.')),
              },
            );
          }}
        >
          <WtFormError message={error} />
          <p className="text-sm text-fieldiq-text">Starting a period ends the open one at the new start time.</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <WtField label="Kind">
              <select className={WT_INPUT} aria-label="Period kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                {PERIOD_KINDS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </WtField>
            <WtField label="Starts (blank = now)">
              <input type="datetime-local" className={WT_INPUT} aria-label="Period start" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} />
            </WtField>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className={WT_SECONDARY} onClick={() => setStarting(false)} disabled={create.isPending}>
              Cancel
            </button>
            <button type="submit" className={WT_PRIMARY} disabled={create.isPending}>
              {create.isPending ? 'Saving…' : 'Start period'}
            </button>
          </div>
        </form>
      )}
      <WtRegister
        query={query}
        rows={periods}
        columns={[
          { key: 'kind', header: 'Kind', render: (p) => <span className="font-extrabold text-white">{periodLabel(p.kind)}</span> },
          { key: 'start', header: 'Started', render: (p) => when(p.startedAt, tz) },
          { key: 'end', header: 'Ended', render: (p) => (p.open ? 'Open' : when(p.endedAt, tz)) },
          { key: 'n', header: 'Readings', align: 'right', render: (p) => String(p.readingCount) },
        ]}
        rail={(p) => (p.open ? 'border-l-fi-amber' : 'border-l-transparent')}
        subject="the test periods"
        testId="wt-period"
        minWidth="min-w-[640px]"
        empty={<WtEmpty>No test period has been started. Readings are recorded without one until a period starts.</WtEmpty>}
        card={(p) => (
          <p className="text-sm text-fieldiq-text">
            <span className="font-extrabold text-white">{periodLabel(p.kind)}</span> · {when(p.startedAt, tz)} → {p.open ? 'open' : when(p.endedAt, tz)} · {p.readingCount} readings
          </p>
        )}
      />
    </WtPanel>
  );
}

// ═══ Chart ════════════════════════════════════════════════════════════════════

const DEFAULT_CHART = ['wellhead_pressure', 'gas_rate'];
const MAX_CHART = 12;

export function ChartSection({ job, meta }: { job: WtJobDetail; meta: WtMeta | null }) {
  const fieldsQuery = useWtReadingFields();
  const fields = fieldsQuery.data?.data?.fields ?? [];
  const [picked, setPicked] = useState<string[]>(DEFAULT_CHART);
  const series = useWtSeries(job.id, picked);
  const tz = meta?.timeZone ?? null;
  const toggle = (key: string) =>
    setPicked((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : cur.length >= MAX_CHART ? cur : [...cur, key]));
  const failure = queryLoadFailure(series);

  return (
    <WtPanel eyebrow="Chart" title="Readings over time" testId="wt-chart">
      <div className="space-y-2 border-b border-fieldiq-border px-4 py-3">
        <p className="text-xs text-fieldiq-muted">Pick up to {MAX_CHART} fields. Each is drawn on its own axis, in the unit shown.</p>
        {fieldsQuery.isLoading ? (
          <WtLoading label="Loading the reading fields" />
        ) : (
          <WtChipRow label="Chart fields">
            {fields.map((f) => (
              <WtChip key={f.key} label={f.label} active={picked.includes(f.key)} onClick={() => toggle(f.key)} />
            ))}
          </WtChipRow>
        )}
      </div>
      {picked.length === 0 ? (
        <WtEmpty>Pick a field to chart.</WtEmpty>
      ) : series.isLoading ? (
        <WtLoading label="Loading the chart" />
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(series)} kind={failure} subject="the chart" />
      ) : (series.data?.data?.fields ?? []).every((f) => f.points.length === 0) ? (
        <WtEmpty>No reading on this job records the picked fields yet.</WtEmpty>
      ) : (
        <div className="grid gap-4 px-4 py-4 xl:grid-cols-2">
          {(series.data?.data?.fields ?? []).map((f) => {
            const data = f.points
              .filter((p) => p.display !== null)
              .map((p) => ({ t: Date.parse(p.readAt), v: p.display as number }));
            return (
              <figure key={f.key} className="rounded-fi-control border border-fieldiq-border p-3" data-testid="wt-chart-field" data-field={f.key}>
                <figcaption className="mb-2 text-sm font-extrabold text-white">
                  {f.label} <span className="font-bold text-fieldiq-muted">({wtUnitLabel(f.displayUnit)})</span>
                </figcaption>
                {data.length === 0 ? (
                  <p className="py-6 text-center text-sm text-fieldiq-muted">No reading records this field yet.</p>
                ) : (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--fieldiq-border)" />
                        <XAxis
                          dataKey="t"
                          type="number"
                          scale="time"
                          domain={['dataMin', 'dataMax']}
                          tick={{ fill: 'var(--fieldiq-muted)', fontSize: 11 }}
                          tickFormatter={(t: number) => formatInstant(new Date(t).toISOString(), tz, { month: 'short', day: 'numeric', hour: 'numeric', hour12: true }) ?? ''}
                        />
                        <YAxis tick={{ fill: 'var(--fieldiq-muted)', fontSize: 11 }} width={56} />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'var(--fieldiq-card)', border: '1px solid var(--fieldiq-border)', borderRadius: 8 }}
                          labelFormatter={(t) => when(new Date(Number(t)).toISOString(), tz)}
                          formatter={(v) => [withUnit(Number(v), wtUnitLabel(f.displayUnit)), f.label]}
                        />
                        <Line type="monotone" dataKey="v" stroke="var(--fi-amber)" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </figure>
            );
          })}
        </div>
      )}
    </WtPanel>
  );
}

// ═══ Flags ════════════════════════════════════════════════════════════════════

export function FlagsSection({ job, meta, canDispatch }: { job: WtJobDetail; meta: WtMeta | null; canDispatch: boolean }) {
  const query = useWtFlags(job.id);
  const fieldsQuery = useWtReadingFields();
  const ack = useAckWtFlag();
  const [acking, setAcking] = useState<WtFlag | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const tz = meta?.timeZone ?? null;
  const metricUnit = (field: string) => fieldsQuery.data?.data?.fields.find((f) => f.key === field)?.metricUnit ?? null;
  const all = query.data?.data ?? [];
  // Open first, each half newest first (the API's order).
  const flags = [...all.filter((f) => !f.acknowledged), ...all.filter((f) => f.acknowledged)];
  const openCount = all.filter((f) => !f.acknowledged).length;

  const failure = queryLoadFailure(query);
  return (
    <WtPanel eyebrow="Flags" title={query.isLoading ? 'Flags' : `Flags · ${openCount} open`} testId="wt-flags">
      {query.isLoading ? (
        <WtLoading label="Loading the flags" />
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the flags" />
      ) : flags.length === 0 ? (
        <WtEmpty>No reading on this job has raised a flag.</WtEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {flags.map((f) => (
            <li
              key={f.id}
              data-testid="wt-flag"
              data-acknowledged={f.acknowledged ? 'true' : 'false'}
              className={`flex flex-wrap items-start justify-between gap-3 border-l-4 px-4 py-3 ${f.acknowledged ? 'border-l-transparent' : 'border-l-fi-red'}`}
            >
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-extrabold text-white">
                  {f.kindLabel} · <span className="font-bold text-fieldiq-muted">{when(f.readAt, tz)}</span>
                </p>
                <p className="text-sm text-fieldiq-text">{wtFlagText(f, metricUnit(f.field))}</p>
                {f.acknowledged && (
                  <p className="text-xs text-fieldiq-muted" data-testid="wt-flag-ack">
                    Acknowledged by {f.acknowledgedByName || '—'} · {when(f.acknowledgedAt, tz)}
                    {f.acknowledgeNote ? ` — ${f.acknowledgeNote}` : ''}
                  </p>
                )}
              </div>
              {!f.acknowledged && canDispatch && (
                <button type="button" className={WT_SECONDARY} onClick={() => { setError(null); setNote(''); setAcking(f); }} data-testid="wt-ack-flag">
                  <CheckCircle2 className="size-4" aria-hidden="true" />
                  Ack
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canDispatch && (
        <WtDialog
          open={!!acking}
          title="Acknowledge flag"
          onClose={() => setAcking(null)}
          footer={
            <>
              <button type="button" className={WT_SECONDARY} onClick={() => setAcking(null)} disabled={ack.isPending}>
                Not now
              </button>
              <button
                type="button"
                className={WT_PRIMARY}
                disabled={ack.isPending}
                onClick={() =>
                  acking &&
                  ack.mutate(
                    { id: acking.id, note: note.trim() || null },
                    {
                      onSuccess: () => {
                        toast.success('Flag acknowledged');
                        setAcking(null);
                      },
                      onError: (err) => setError(wtRefusal(err, 'The flag could not be acknowledged.')),
                    },
                  )
                }
              >
                {ack.isPending ? 'Saving…' : 'Acknowledge'}
              </button>
            </>
          }
        >
          <WtFormError message={error} />
          {acking && <p className="text-sm text-fieldiq-text">{wtFlagText(acking, metricUnit(acking.field))}</p>}
          <div className="mt-3">
            <WtField label="Note (optional)">
              <textarea className={`${WT_INPUT} h-20 py-2`} aria-label="Acknowledge note" value={note} onChange={(e) => setNote(e.target.value)} />
            </WtField>
          </div>
        </WtDialog>
      )}
    </WtPanel>
  );
}

// ═══ Daily volumes ════════════════════════════════════════════════════════════

const DISPOSITIONS: { key: WtDisposition; label: string }[] = [
  { key: 'flared', label: 'Flared' },
  { key: 'vented', label: 'Vented' },
  { key: 'conserved', label: 'Conserved' },
];
const GAS_UNITS = ['e3m3', 'mcf'];

/** The computed figure in the tenant's gas unit, as the API converted it (`<kind>Display` + `displayUnit`). */
const computedText = (c: WtDailyVolume['computed'], key: WtDisposition) =>
  withUnit(c[`${key}Display`], wtUnitLabel(c.displayUnit));

export function VolumesSection({ job, meta, canDispatch }: { job: WtJobDetail; meta: WtMeta | null; canDispatch: boolean }) {
  const query = useWtDailyVolumes(job.id);
  const save = useSaveWtDailyVolume();
  const [editing, setEditing] = useState<string | null>(null);
  const [day, setDay] = useState('');
  const defUnit = meta?.unitSystem === 'imperial' ? 'mcf' : 'e3m3';
  const [form, setForm] = useState<Record<WtDisposition, { value: string; unit: string }>>({
    flared: { value: '', unit: defUnit },
    vented: { value: '', unit: defUnit },
    conserved: { value: '', unit: defUnit },
  });
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const days = query.data?.data?.days ?? [];
  const totals = query.data?.data?.totals ?? null;
  const canWrite = canDispatch && job.status !== 'draft' && job.status !== 'cancelled';
  const failure = queryLoadFailure(query);

  const startEdit = (d: WtDailyVolume | null) => {
    setError(null);
    setDay(d?.workDate ?? meta?.today ?? '');
    setNotes(d?.notes ?? '');
    const next = {} as Record<WtDisposition, { value: string; unit: string }>;
    for (const x of DISPOSITIONS) {
      const e = d?.entered?.[x.key];
      next[x.key] = { value: e?.entered.value ?? '', unit: e?.entered.unit ?? defUnit };
    }
    setForm(next);
    setEditing(d?.workDate ?? 'new');
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!day) return setError('Pick the day these volumes are for.');
    if (DISPOSITIONS.every((x) => form[x.key].value.trim() === '')) return setError('Enter at least one of flared, vented or conserved.');
    const body: Record<string, unknown> = { work_date: day, source: 'entered', notes: notes.trim() || null };
    for (const x of DISPOSITIONS) {
      const v = form[x.key].value.trim();
      body[x.key] = v === '' ? null : v;
      if (v !== '') body[`${x.key}_unit`] = form[x.key].unit;
    }
    save.mutate(
      { jobId: job.id, body: body as never },
      {
        onSuccess: () => {
          toast.success('Volumes saved');
          setEditing(null);
        },
        onError: (err) => setError(wtRefusal(err, 'The volumes could not be saved.', WT_VOLUME_REFUSALS)),
      },
    );
  };

  const resetToComputed = (d: WtDailyVolume) =>
    save.mutate(
      { jobId: job.id, body: { work_date: d.workDate, source: 'computed' } },
      {
        onSuccess: () => toast.success('Back to the computed figures'),
        onError: (err) => setError(wtRefusal(err, 'The day could not be reset.', WT_VOLUME_REFUSALS)),
      },
    );

  return (
    <WtPanel
      eyebrow="Daily volumes"
      title="Flared, vented & conserved"
      testId="wt-volumes"
      actions={
        canWrite && !editing ? (
          <button type="button" className={WT_PRIMARY} onClick={() => startEdit(null)} data-testid="wt-enter-volumes">
            <Plus className="size-4" aria-hidden="true" />
            Enter a day
          </button>
        ) : undefined
      }
    >
      <p className="border-b border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
        Each day shows the office's entry where one was made, otherwise the figures computed from the readings.
      </p>
      {error && !editing && (
        <div className="px-4 pt-3">
          <WtFormError message={error} />
        </div>
      )}
      {editing && (
        <form className="space-y-3 border-b border-fieldiq-border px-4 py-4" onSubmit={submit} data-testid="wt-volume-form">
          <WtFormError message={error} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <WtField label="Day">
              <input type="date" className={WT_INPUT} aria-label="Day" value={day} disabled={editing !== 'new'} onChange={(e) => setDay(e.target.value)} />
            </WtField>
            {DISPOSITIONS.map((x) => (
              <WtField key={x.key} label={x.label}>
                <div className="flex gap-2">
                  <input
                    className={`${WT_INPUT} min-w-0 flex-1`}
                    aria-label={x.label}
                    inputMode="decimal"
                    value={form[x.key].value}
                    onChange={(e) => setForm((f) => ({ ...f, [x.key]: { ...f[x.key], value: e.target.value } }))}
                  />
                  <select
                    className={`${WT_INPUT} w-24 shrink-0`}
                    aria-label={`${x.label} unit`}
                    value={form[x.key].unit}
                    onChange={(e) => setForm((f) => ({ ...f, [x.key]: { ...f[x.key], unit: e.target.value } }))}
                  >
                    {GAS_UNITS.map((u) => (
                      <option key={u} value={u}>
                        {wtUnitLabel(u)}
                      </option>
                    ))}
                  </select>
                </div>
              </WtField>
            ))}
          </div>
          <WtField label="Notes">
            <input className={WT_INPUT} aria-label="Volume notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </WtField>
          <div className="flex justify-end gap-2">
            <button type="button" className={WT_SECONDARY} onClick={() => setEditing(null)} disabled={save.isPending}>
              Cancel
            </button>
            <button type="submit" className={WT_PRIMARY} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save volumes'}
            </button>
          </div>
        </form>
      )}
      {query.isLoading ? (
        <WtLoading label="Loading the daily volumes" />
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the daily volumes" />
      ) : days.length === 0 ? (
        <WtEmpty>No day has volumes yet. They are computed from the readings as they come in.</WtEmpty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left" data-testid="wt-volume-table">
            <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-4 py-3">Day</th>
                <th scope="col" className="px-4 py-3">Source</th>
                {DISPOSITIONS.map((x) => (
                  <th key={x.key} scope="col" className="px-4 py-3 text-right">{x.label}</th>
                ))}
                {DISPOSITIONS.map((x) => (
                  <th key={`c-${x.key}`} scope="col" className="px-4 py-3 text-right">{x.label} (computed)</th>
                ))}
                {canWrite && <th scope="col" className="sticky right-0 bg-fieldiq-card px-4 py-3"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.id} className="border-t border-fieldiq-border" data-testid="wt-volume-row" data-source={d.source}>
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-extrabold text-white">{wtDay(d.workDate)}</td>
                  <td className="px-4 py-3 text-sm text-fieldiq-text">{d.source === 'entered' ? 'Entered' : 'Computed'}</td>
                  {DISPOSITIONS.map((x) => (
                    <td key={x.key} className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">
                      {d.source === 'entered' && d.entered[x.key] ? wtQuantity(d.entered[x.key]) : wtShown(d.shown[x.key])}
                    </td>
                  ))}
                  {DISPOSITIONS.map((x) => (
                    <td key={`c-${x.key}`} className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-muted">
                      {computedText(d.computed, x.key)}
                    </td>
                  ))}
                  {canWrite && (
                    <td className="sticky right-0 whitespace-nowrap bg-fieldiq-card px-4 py-3 text-right">
                      <button type="button" className={WT_SECONDARY} onClick={() => startEdit(d)} disabled={!!editing}>
                        {d.source === 'entered' ? 'Edit' : 'Override'}
                      </button>
                      {d.source === 'entered' && (
                        <button type="button" className={`${WT_SECONDARY} ml-2`} onClick={() => resetToComputed(d)} disabled={save.isPending}>
                          Use computed
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-fieldiq-border">
              <tr data-testid="wt-volume-totals">
                <th scope="row" className="px-4 py-3 text-sm font-black text-white" colSpan={2}>
                  Total
                </th>
                {DISPOSITIONS.map((x) => (
                  <td key={x.key} className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm font-extrabold tabular-nums text-white">
                    {wtShown(totals?.[x.key])}
                  </td>
                ))}
                <td colSpan={3 + (canWrite ? 1 : 0)} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </WtPanel>
  );
}
